import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ChevronRight, MessageSquare, Zap, Play, CircleCheck as CheckCircle2, Clock, CircleX } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { EnrichedObjective, EnrichedObjectiveContact } from '../../pages/app/MissionControl';
import type { MCObjectiveStatus, MCContactStatus } from '../../types';

interface Props {
  objectives: EnrichedObjective[];
  readOnly?: boolean;
  onStatusChanged?: () => void;
}

const STATUS_CONFIG: Record<string, { style: string; label: string; icon: typeof Clock }> = {
  pending: { style: 'border-app-line bg-app-surface', label: 'Pending', icon: Clock },
  in_progress: { style: 'border-app-warning bg-app-warning/10', label: 'In Progress', icon: Play },
  completed: { style: 'border-forest-400 bg-forest-50/40', label: 'Completed', icon: CheckCircle2 },
  incomplete: { style: 'border-app-critical bg-app-critical/10', label: 'Incomplete', icon: CircleX },
};

const STATUS_DOT: Record<string, string> = {
  pending: 'bg-app-line-strong',
  in_progress: 'bg-app-warning',
  completed: 'bg-forest-500',
  incomplete: 'bg-app-critical',
};

function StatusDropdown({ currentStatus, onUpdate, type }: {
  currentStatus: string;
  onUpdate: (status: string, notes: string) => void;
  type: 'objective' | 'contact';
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');

  const statuses: string[] = type === 'objective'
    ? ['pending', 'in_progress', 'completed', 'incomplete']
    : ['pending', 'completed', 'incomplete'];

  const current = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.pending;
  const CurrentIcon = current.icon;

  function handleSelect(status: string) {
    onUpdate(status, notes);
    setNotes('');
    setOpen(false);
  }

  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition border ${
          currentStatus === 'pending' ? 'border-app-line bg-app-canvas text-app-muted hover:bg-app-canvas' :
          currentStatus === 'in_progress' ? 'border-app-warning bg-app-warning/10 text-app-warning hover:bg-app-warning/10' :
          currentStatus === 'completed' ? 'border-forest-200 bg-forest-50 text-forest-700 hover:bg-forest-100' :
          'border-app-critical bg-app-critical/10 text-app-critical hover:bg-app-critical/10'
        }`}
      >
        <CurrentIcon className="w-3 h-3" />
        {current.label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-50 bg-app-surface rounded-lg shadow-none border border-app-line p-3 w-56">
            <div className="space-y-1 mb-3">
              {statuses.map(s => {
                const cfg = STATUS_CONFIG[s];
                const Icon = cfg.icon;
                return (
                  <button
                    key={s}
                    onClick={() => handleSelect(s)}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors ${
                      s === currentStatus ? 'bg-forest-50 text-forest-700 font-medium' : 'text-app-muted hover:bg-app-canvas'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add a note (optional)..."
              rows={2}
              className="w-full rounded-lg border border-app-line px-2.5 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-forest-500 resize-none"
            />
          </div>
        </>
      )}
    </div>
  );
}

export default function ObjectiveTreeView({ objectives, readOnly, onStatusChanged }: Props) {
  const navigate = useNavigate();
  const { apiPut } = useApi();

  async function updateObjectiveStatus(objId: string, status: string, notes: string) {
    await apiPut(`/v1/mission-control/objectives/${objId}`, notes ? { status, notes } : { status });
    onStatusChanged?.();
  }

  async function updateContactStatus(contactObjId: string, status: string, notes: string) {
    await apiPut(`/v1/mission-control/objective-contacts/${contactObjId}`, notes ? { status, notes } : { status });
    onStatusChanged?.();
  }

  if (objectives.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-app-muted">No objectives defined for this cycle yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {objectives.map(obj => {
        const completedContacts = obj.contacts.filter(c => c.status === 'completed').length;
        const totalContacts = obj.contacts.length;
        const progressPct = totalContacts > 0 ? (completedContacts / totalContacts) * 100 : 0;
        const isPending = obj.status === 'pending';

        return (
          <div key={obj.id} className={`rounded-lg border-2 transition-colors ${STATUS_CONFIG[obj.status]?.style || STATUS_CONFIG.pending.style}`}>
            <div className="flex items-stretch">
              {/* Account node */}
              <div className="flex-shrink-0 w-60 p-4 border-r border-app-line">
                <div className="flex items-center gap-2 mb-2">
                  {!readOnly && (
                    <StatusDropdown
                      currentStatus={obj.status}
                      onUpdate={(s, n) => updateObjectiveStatus(obj.id, s as MCObjectiveStatus, n)}
                      type="objective"
                    />
                  )}
                  {readOnly && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium ${
                      STATUS_CONFIG[obj.status]?.style || ''
                    }`}>
                      {STATUS_CONFIG[obj.status]?.label}
                    </span>
                  )}
                </div>

                <h4
                  onClick={() => navigate(`/app/accounts/${obj.account.id}`)}
                  className="text-sm font-semibold text-app-ink truncate cursor-pointer hover:text-forest-700 transition-colors mb-1"
                >
                  {obj.account.company_name}
                </h4>

                {obj.account.tier && (
                  <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full mb-2 ${
                    obj.account.tier === 'A' ? 'bg-forest-50 text-forest-700' :
                    obj.account.tier === 'B' ? 'bg-app-brand/10 text-app-brand' :
                    'bg-app-warning/10 text-app-warning'
                  }`}>Tier {obj.account.tier}</span>
                )}

                {/* Progress bar */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[10px] text-app-muted mb-1">
                    <span>{completedContacts}/{totalContacts} contacts</span>
                    <span>{Math.round(progressPct)}%</span>
                  </div>
                  <div className="h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${obj.account.company_name} contact progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressPct)}>
                    <div
                      className="h-full rounded-full bg-app-accent transition duration-300"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>

                {!obj.use_cases_processed && (
                  <button
                    onClick={() => navigate(`/app/accounts/${obj.account.id}`)}
                    className="mt-2 flex items-center gap-1 text-[10px] text-app-warning hover:text-app-warning"
                  >
                    <Zap className="w-3 h-3" />
                    Process messaging
                  </button>
                )}

                {obj.notes && (
                  <div className="mt-2 flex items-start gap-1">
                    <MessageSquare className="w-3 h-3 text-app-muted mt-0.5 shrink-0" />
                    <p className="text-[10px] text-app-muted line-clamp-2">{obj.notes}</p>
                  </div>
                )}
              </div>

              {/* Contact nodes */}
              <div className="flex-1 py-3 px-4">
                {isPending && totalContacts > 0 && (
                  <p className="text-[10px] text-app-muted mb-2 italic">
                    Set account to "In Progress" to start working contacts
                  </p>
                )}

                <div className="space-y-1.5">
                  {obj.contacts.map(oc => (
                    <ContactRow
                      key={oc.id}
                      oc={oc}
                      readOnly={readOnly}
                      accountPending={isPending}
                      onUpdateStatus={(s, n) => updateContactStatus(oc.id, s as MCContactStatus, n)}
                      onNavigate={() => navigate(`/app/contacts?contactId=${oc.contact.id}`)}
                    />
                  ))}
                  {totalContacts === 0 && (
                    <p className="text-xs text-app-muted py-2">No contact targets defined</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContactRow({ oc, readOnly, accountPending, onUpdateStatus, onNavigate }: {
  oc: EnrichedObjectiveContact;
  readOnly?: boolean;
  accountPending: boolean;
  onUpdateStatus: (status: string, notes: string) => void;
  onNavigate: () => void;
}) {
  const contactStatus = STATUS_CONFIG[oc.status] || STATUS_CONFIG.pending;
  const isCompleted = oc.status === 'completed';
  const isIncomplete = oc.status === 'incomplete';

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
        accountPending && oc.status === 'pending'
          ? 'border-app-line bg-app-canvas/50 opacity-60'
          : isCompleted
            ? 'border-forest-200 bg-forest-50/30'
            : isIncomplete
              ? 'border-app-critical bg-app-critical/10'
              : 'border-app-line bg-app-surface hover:border-app-line-strong'
      }`}
    >
      {/* Status control */}
      {!readOnly && !accountPending ? (
        <StatusDropdown
          currentStatus={oc.status}
          onUpdate={onUpdateStatus}
          type="contact"
        />
      ) : (
        <span className={`w-3 h-3 rounded-full shrink-0 ${STATUS_DOT[oc.status]}`} />
      )}

      {/* Contact info - clickable */}
      <div
        className="min-w-0 flex-1 cursor-pointer"
        onClick={onNavigate}
      >
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium truncate ${
            accountPending && oc.status === 'pending' ? 'text-app-muted' : 'text-app-ink'
          }`}>
            {oc.contact.first_name} {oc.contact.last_name}
          </p>
          {isCompleted && <CheckCircle2 className="w-3.5 h-3.5 text-forest-500 shrink-0" />}
          {isIncomplete && <CircleX className="w-3.5 h-3.5 text-app-critical shrink-0" />}
        </div>
        <p className="text-[10px] text-app-muted truncate">{oc.contact.role || 'No role'}</p>
      </div>

      {oc.notes && (
        <div className="flex items-center gap-1 shrink-0" title={oc.notes}>
          <MessageSquare className="w-3 h-3 text-app-muted" />
        </div>
      )}

      <ChevronRight
        className="w-3.5 h-3.5 text-app-line-strong shrink-0 cursor-pointer hover:text-forest-500 transition-colors"
        onClick={onNavigate}
      />
    </div>
  );
}
